<!-- eslint-disable vue/no-v-model-argument -->
<template>
  <AntRow class="management">
    <AntCol :span="24">
      <!-- <p><b>searchText:</b> {{ searchText }}</p> -->
      <AntRow
        justify="space-between"
        class="search-row"
      >
        <AntCol :span="12">
          <AntRow :gutter="16">
            <AntCol :span="20">
              <AntInput
                v-model:value="searchText"
                placeholder="Search"
                allow-clear
                @pressEnter="onSearch"
              />
            </AntCol>

            <AntCol :span="4">
              <AntButton
                type="primary"
                html-type="submit"
                @click="onSearch"
              >
                <template #icon>
                  <SearchIcon />
                </template>
                Search
              </AntButton>
            </AntCol>
          </AntRow>
        </AntCol>

        <AntButton
          v-if="!readOnly"
          type="primary"
          html-type="submit"
          @click="onAdd"
        >
          <template #icon>
            <AddIcon />
          </template>
          Add
        </AntButton>
      </AntRow>

      <div v-if="isDev">
        editableData: <pre style="font-size: 10px; max-height: 300px; overflow-y: auto;">{{ JSON.stringify(editableData, null, 2) }}</pre>
        <div style="display: flex;">
          <div style="width: 50%;">
            platformsClientsData: <pre style="font-size: 10px; max-height: 300px; overflow-y: auto;">{{ JSON.stringify(platformsClientsData, null, 2) }}</pre>
          </div>
          <div style="width: 50%;">
            dataSource: <pre style="font-size: 10px; max-height: 300px; overflow-y: auto;">{{ JSON.stringify(dataSource, null, 2) }}</pre>
          </div>
        </div>
      </div>

      <AntTable
        :columns="columns"
        :data-source="displayedDataSource"
        :pagination="pagination"
        @change="onTableChange"
      >
        <template #headerCell="{ title, column }">
          <template v-if="column.dataIndex === 'brandText'">
            {{ title }}
            <AntTooltip placement="top">
              <template #title>
                <span v-pre>API 文件內 {{brand}} 替代字樣</span>
              </template>
              <QuestionIcon />
            </AntTooltip>
          </template>
        </template>

        <template #bodyCell="{ text, column, record }">
          <template v-if="column.dataIndex === nameField">
            <AntInput
              v-if="editableData[record.key]"
              v-model:value="editableData[record.key][column.dataIndex]"
              :placeholder="column.title"
              allow-clear
              @blur="e => handleNewId(record)"
            />
            <div v-else>
              {{ text }}
            </div>
          </template>

          <template v-if="column.dataIndex === 'remark'">
            <AntTextarea
              v-if="editableData[record.key]"
              v-model:value="editableData[record.key][column.dataIndex]"
              :rows="3"
              allow-clear
            />
            <div
              v-else
              style="max-height: 60px; overflow-y: auto;"
              v-html="(text || '').replaceAll('\n', '<br/>')"
            />
          </template>

          <template v-if="column.dataIndex === 'action'">
            <div class="action-icons">
              <span v-if="editableData[record.key]">
                <SaveIcon
                  v-if="editableData[record.key][nameField]"
                  title="Save"
                  @click="onSave(record)"
                />
                <CancelIcon
                  title="Cancel"
                  @click="onCancel(record)"
                />
              </span>
              <span v-else>
                <EditIcon
                  title="Edit"
                  @click="onEdit(record.key)"
                />
                <AntPopconfirm
                  :title="`Sure to delete ${record[nameField]}?`"
                  @confirm="e => onDelete(record)"
                >
                  <DeleteIcon title="Delete" />
                </AntPopconfirm>
              </span>
            </div>
          </template>
        </template>
      </AntTable>
    </AntCol>
  </AntRow>
</template>

<script>
import {
  ref,
  reactive,
  inject,
  watch,
  computed,
  nextTick,
} from 'vue'

import moment from 'moment'
import ShortUniqueId from 'short-unique-id'

import {
  Row as AntRow,
  Col as AntCol,
  Table as AntTable,
  Input as AntInput,
  Button as AntButton,
  Tooltip as AntTooltip,
  Popconfirm as AntPopconfirm,
  message as antMessage,
} from 'ant-design-vue'

import AddIcon from '@ant-design/icons-vue/lib/icons/PlusOutlined'
import EditIcon from '@ant-design/icons-vue/lib/icons/EditOutlined'
import DeleteIcon from '@ant-design/icons-vue/lib/icons/DeleteOutlined'
import SaveIcon from '@ant-design/icons-vue/lib/icons/CheckOutlined'
import CancelIcon from '@ant-design/icons-vue/lib/icons/CloseOutlined'
import SearchIcon from '@ant-design/icons-vue/lib/icons/SearchOutlined'
import QuestionIcon from '@ant-design/icons-vue/lib/icons/QuestionCircleOutlined'

import {
  createManagementItem,
  updateManagementItem,
  deleteManagementItem,
} from '@/api/apiDocV3'

import { titleCase } from '@/utils'

export default {
  name: 'PlatformClientManagement',
  components: {
    AntRow,
    AntCol,
    AntTable,
    AntInput,
    AntTextarea: AntInput.TextArea,
    AntButton,
    // AntSelect,
    AntTooltip,
    AntPopconfirm,
    AddIcon,
    EditIcon,
    DeleteIcon,
    SaveIcon,
    CancelIcon,
    SearchIcon,
    QuestionIcon,
  },
  props: {
    type: String,
    readOnly: Boolean,
    platformsClientsData: Array,
    onChangeManagementData: Function,
  },
  emits: ['beforeDelete'],
  setup(props, { emit, expose }) {
    const isDev = inject('isDev')

    const searchText = ref('')
    const platformsClientsData = reactive(props.platformsClientsData)
    const nameField = computed(() => `${props.type}Name`)

    const pagination = ref({
      current: 1,
      pageSize: 50,
      pageSizeOptions: ['50', '100', '300'],
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    })

    const columns = computed(() => ([
      {
        title: titleCase(nameField.value),
        dataIndex: nameField.value,
        width: '250px',
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
      },
      {
        title: 'Created Time',
        dataIndex: 'createdTime',
        width: '180px',
        customRender: ({ text }) => (text ? moment(text).format('YYYY/MM/DD HH:mm:ss') : ''),
      },
      ...(props.readOnly ? [] : [{
        title: 'Action',
        dataIndex: 'action',
        align: 'center',
        width: '100px',
      }]),
    ]))

    const setDataSource = () => props.platformsClientsData.map(d => ({ ...d, key: d.id }))
    const dataSource = ref(setDataSource())
    const displayedDataSource = ref(dataSource.value)

    watch(() => props.type, () => {
      dataSource.value = setDataSource()
      displayedDataSource.value = dataSource.value
    })

    const editableData = reactive({})
    const editableFields = computed(() => [nameField.value, 'remark'])

    const onTableChange = ({ current, pageSize }) => {
      pagination.value.current = current
      pagination.value.pageSize = pageSize
    }

    const onSearch = () => {
      console.log('onSearch', searchText.value)
      displayedDataSource.value = dataSource.value.filter((d) => {
        console.log('d[nameField.value]', d[nameField.value])
        const matched = d[nameField.value].toLowerCase().includes(searchText.value.toLowerCase()) || (d.remark || '').toLowerCase().includes(searchText.value.toLowerCase())
        return matched
      })
    }

    const handleNewId = (record) => {
      console.log('handleNewId', record)
      if (record.isNew && editableData[record.key][nameField.value]) {
        const uniqueId = record.key.split('_').pop()
        const newId = `${editableData[record.key][nameField.value]}_${uniqueId}`
        editableData[newId] = editableData[record.key]
        delete editableData[record.key]
        record.key = newId
        record.id = record.key
      }
    }

    const performDelete = async ({ entityType, id, record }) => {
      const messageKey = `${Date.now()}`
      antMessage.loading('', { key: messageKey })

      try {
        const { data, message } = await deleteManagementItem({ entityType, id })

        if (data) {
          delete editableData[id]
          dataSource.value = dataSource.value.filter(d => d.id !== id)
          displayedDataSource.value = dataSource.value

          const idx = platformsClientsData.findIndex(d => d.id === id)
          platformsClientsData.splice(idx, 1)
          props.onChangeManagementData(entityType, platformsClientsData)

          antMessage.success({
            key: messageKey,
            content: `Delete ${entityType} [${record[`${entityType}Name`]}] successfully`,
          })
          return true
        }
        antMessage.error({
          key: messageKey,
          duration: 5,
          content: `Failed to delete ${entityType} [${record[`${entityType}Name`]}]: ${message}`,
        })
        return false
      } catch (error) {
        antMessage.destroy(messageKey)
        antMessage.error({
          content: `刪除時發生錯誤: ${error.message}`,
          duration: 5,
        })
        return false
      }
    }

    const onDelete = async (record) => {
      const { id } = record
      console.log('onDelete', { id, type: props.type })

      // 先觸發 beforeDelete 事件，讓父組件檢查是否有受影響的 sections
      emit('beforeDelete', { entityType: props.type, id, record })

      // 如果有受影響的 sections，會顯示 modal，onDelete 不執行刪除
      // 如果沒有受影響的 sections，onBeforeDeletePlatformClient 會直接 return，這裡也不執行刪除
      // 實際刪除會在 confirmDelete 中執行（modal 確認後）或由父組件直接處理（沒有受影響時）
    }

    // 用於 modal 確認後執行刪除
    const confirmDelete = async (deleteInfo) => {
      if (!deleteInfo) return false
      return performDelete(deleteInfo)
    }

    expose({ confirmDelete })

    const onEdit = (editKey) => {
      console.log('onEdit editKey', editKey)
      const data = dataSource.value.find(d => editKey === d.key)
      editableData[editKey] = Object.keys(data).reduce((acc, key) => {
        if (editableFields.value.includes(key)) acc[key] = data[key]
        return acc
      }, {})
    }

    const onAdd = () => {
      if (pagination.value.current !== 1) pagination.value.current = 1

      const newData = {
        key: new ShortUniqueId({ length: 10 }).rnd(),
        isNew: true,
      }
      editableData[newData.key] = newData
      dataSource.value.splice(0, 0, editableData[newData.key])
      displayedDataSource.value = dataSource.value

      nextTick(() => {
        const input = document.querySelector('.ant-table-tbody tr:first-child input')
        if (input) input.focus()
      })
    }

    const isSameEditableValue = (a, b) => {
      if (a == null && b == null) return true
      if (a == null) return b === ''
      if (b == null) return a === ''
      return a === b
    }

    const onSave = async (record) => {
      const { key } = record
      const type = editableData[key].isNew ? 'create' : 'update'
      if (type === 'update') {
        const unchanged = editableFields.value.every(field => (
          isSameEditableValue(editableData[key][field], record[field])
        ))
        if (unchanged) {
          delete editableData[key]
          return
        }
      }

      const messageKey = Date.now()
      antMessage.loading('', { key: messageKey })

      if (type === 'create') editableData[key].createdTime = moment().toISOString(true)

      const updateData = { ...editableData[key], id: key, entityType: props.type }
      delete updateData.key
      delete updateData.isNew

      let data
      let message
      if (type === 'create') {
        ({ data, message } = await createManagementItem(updateData))
      } else {
        ({ data, message } = await updateManagementItem(updateData))
      }

      const findData = dataSource.value.find(d => key === d.key)

      if (data) {
        Object.assign(findData, editableData[key])
        delete editableData[key]

        antMessage.success({
          key: messageKey,
          content: `${titleCase(type)} ${props.type} [${record[nameField.value]}] successfully`,
        })

        if (type === 'create') {
          // 以 platformName / clientName 字母順序插入資料
          const entityNameField = `${props.type}Name`
          const idxToInsert = platformsClientsData.findIndex(
            d => (d[entityNameField] || '').localeCompare(data[entityNameField] || '') > 0,
          )
          if (idxToInsert > 0) {
            platformsClientsData.splice(idxToInsert, 0, data)
          } else {
            platformsClientsData.push(data)
          }

          delete record.isNew
        } else {
          const idx = platformsClientsData.findIndex(d => d.id === key)
          platformsClientsData[idx] = data
        }
        props.onChangeManagementData(props.type, platformsClientsData)
      } else {
        antMessage.error({
          key: messageKey,
          duration: 5,
          content: `Failed to ${type} ${props.type} [${record[nameField.value]}]: ${message}`,
        })
      }
    }

    const onCancel = async (record) => {
      const { key } = record
      if (record.isNew) {
        dataSource.value.splice(0, 1)
        displayedDataSource.value = dataSource.value
      }
      delete editableData[key]
    }

    return {
      isDev,
      searchText,
      onSearch,
      onAdd,
      platformsClientsData, // eslint-disable-line vue/no-dupe-keys
      dataSource,
      displayedDataSource,
      pagination,
      onTableChange,
      columns,
      editableData,
      nameField,
      handleNewId,
      onDelete,
      onEdit,
      onSave,
      onCancel,
    }
  },
}
</script>

<style scoped>
.search-row {
  margin-bottom: 40px;
}
</style>

<style>
.management .action-icons .anticon {
  cursor: pointer;
  margin: 0 6px;
  font-size: 16px;
}

.management .action-icons .anticon:hover {
  color: #1890ff;
}
</style>
